import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import toast from 'react-hot-toast';
import { useAuth } from '../../../contexts/AuthContext';
import { playerAPI } from '../../../services/api';

const Form = styled.form`
  margin-top: 0.75rem;
  padding: 1rem 1.1rem;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #fff;
`;

const Field = styled.label`
  display: block;
  margin-bottom: 0.85rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  display: block;
  width: 100%;
  margin-top: 0.35rem;
  padding: 0.65rem 0.75rem;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  font-size: 0.95rem;
  box-sizing: border-box;
  &:disabled {
    background: #f3f4f6;
    color: #6b7280;
  }
`;

const TextArea = styled.textarea`
  display: block;
  width: 100%;
  margin-top: 0.35rem;
  min-height: 120px;
  padding: 0.65rem 0.75rem;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  font-size: 0.95rem;
  resize: vertical;
  box-sizing: border-box;
`;

const Select = styled.select`
  display: block;
  width: 100%;
  margin-top: 0.35rem;
  padding: 0.65rem 0.75rem;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  font-size: 0.95rem;
  box-sizing: border-box;
  background: #fff;
`;

const FieldHint = styled.span`
  display: block;
  margin-top: 0.35rem;
  font-size: 0.8rem;
  font-weight: 400;
  color: #64748b;
  line-height: 1.4;
`;

const SubmitBtn = styled.button`
  padding: 0.75rem 1.25rem;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, #043663, #0e7490);
  color: #fff;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const Success = styled.p`
  margin: 0;
  padding: 0.85rem 1rem;
  border-radius: 10px;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  color: #065f46;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const Error = styled.p`
  margin: 0 0 0.75rem;
  color: #b91c1c;
  font-size: 0.875rem;
`;

export default function HelpSupportForm({
  nodeId,
  supportPreset,
  teamContext = null,
  match = null,
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [submitted, setSubmitted] = useState(null);

  const defaultName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() ||
    user?.name ||
    '';
  const defaultEmail = user?.email || '';

  const subjectDefault = supportPreset?.subjectKey
    ? t(supportPreset.subjectKey)
    : t('phase2.playerHelpWizard.supportSubjects.other');

  const bodyPrefix = supportPreset?.bodyPrefixKey ? t(supportPreset.bodyPrefixKey) : '';

  const [subject, setSubject] = useState(subjectDefault);
  const [message, setMessage] = useState(bodyPrefix ? `${bodyPrefix}\n\n` : '');
  const [selectedTeamId, setSelectedTeamId] = useState(teamContext?.teamId || '');

  useEffect(() => {
    if (teamContext?.teamId) {
      setSelectedTeamId(teamContext.teamId);
    }
  }, [teamContext?.teamId]);

  const { data: teamsRes, isLoading: teamsLoading } = useQuery(
    ['playerTeams', 'supportForm'],
    () => playerAPI.getTeams(),
    { staleTime: 60_000 }
  );

  const teams = useMemo(() => {
    const raw = teamsRes?.data;
    const list = Array.isArray(raw) ? raw : raw?.data || [];
    return list.filter((row) => row?.teamId);
  }, [teamsRes]);

  const showTeamPicker = teams.length > 0;

  const mutation = useMutation(
    (payload) => playerAPI.createSupportTicket(payload),
    {
      onSuccess: (res) => {
        const ref = res?.data?.ticketReference || res?.data?.ticketId;
        setSubmitted(ref);
        toast.success(t('phase2.playerHelpWizard.support.success'));
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || t('phase2.playerHelpWizard.support.error'));
      },
    }
  );

  if (submitted) {
    return (
      <Success>
        {t('phase2.playerHelpWizard.support.successDetail', { reference: submitted })}
      </Success>
    );
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    mutation.mutate({
      subject: subject.trim(),
      message: message.trim(),
      teamId: selectedTeamId || null,
      matchId: match?.id || null,
      helpFlowNode: nodeId,
      category: supportPreset?.category || 'other',
    });
  };

  return (
    <Form onSubmit={handleSubmit}>
      {mutation.isError ? (
        <Error>{mutation.error?.response?.data?.message || t('phase2.playerHelpWizard.support.error')}</Error>
      ) : null}
      <Field>
        {t('phase2.playerHelpWizard.support.name')}
        <Input type="text" value={defaultName} disabled readOnly />
      </Field>
      <Field>
        {t('phase2.playerHelpWizard.support.email')}
        <Input type="email" value={defaultEmail} disabled readOnly />
      </Field>
      {showTeamPicker ? (
        <Field>
          {t('phase2.playerHelpWizard.support.teamOptional')}
          <Select
            value={selectedTeamId}
            onChange={(e) => setSelectedTeamId(e.target.value)}
            disabled={teamsLoading}
          >
            <option value="">{t('phase2.playerHelpWizard.support.teamNone')}</option>
            {teams.map((team) => (
              <option key={team.teamId} value={team.teamId}>
                {team.teamName}
                {team.latestSeasonName ? ` — ${team.latestSeasonName}` : ''}
              </option>
            ))}
          </Select>
          <FieldHint>{t('phase2.playerHelpWizard.support.teamHint')}</FieldHint>
        </Field>
      ) : null}
      <Field>
        {t('phase2.playerHelpWizard.support.subject')}
        <Input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          required
          minLength={3}
        />
      </Field>
      <Field>
        {t('phase2.playerHelpWizard.support.message')}
        <TextArea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          required
          minLength={10}
          placeholder={t('phase2.playerHelpWizard.support.messagePlaceholder')}
        />
      </Field>
      <SubmitBtn type="submit" disabled={mutation.isLoading}>
        {mutation.isLoading ? t('phase2.playerHelpWizard.support.submitting') : t('phase2.playerHelpWizard.support.submit')}
      </SubmitBtn>
    </Form>
  );
}
